import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Col, Container, media, Row, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {InViewAnimContainer} from 'UI/components/layout/InViewAnimContainer';
import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {PayloadLexical} from 'UI/components/layout/PayloadLexical';
import {H5, H6} from 'UI/components/layout/Text';

import {ImageViewAnimation} from 'UI/animations/images';
import {FadeInAnimation} from 'UI/animations/shared';

import {useFeatures} from './useFeatures';
import {transient} from 'Application/configs/emotionTransientProps';
import {isDefined, isDefinedAndPopulated} from 'GlobalShared/utils/typeGuards';

import type {FeaturesBlock} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the shape of the properties object expected by the `Features` component.
 * It includes the feature content array from the Payload CMS schema, a test identifier, and a theme attribute.
 * This interface ensures type safety between the CMS-managed feature data and the component's rendering logic.
 */
interface ComponentProps {
    /**
     * An array of feature objects each containing an image, icon, overline, title, and description for rendering in the grid.
     */
    features: FeaturesBlock['features'];
    /**
     * A unique identifier assigned to the component for testing purposes, enabling reliable element targeting in automated tests.
     */
    testId: string;
    /**
     * The theme data attribute applied to the component's container for switching between color schemes and styling contexts.
     */
    theme: FeaturesBlock['theme'];
}

/**
 * The `Features` component renders a responsive grid of feature cards, each containing an image with an icon overlay, an overline, a title, and a description.
 * It dynamically maps over the provided features array and applies scroll-triggered motion animations to images and text elements as they enter the viewport.
 * The component uses the `useFeatures` hook to retrieve theme colors and structures the layout in a CSS grid that adapts from single-column on mobile to a two-column layout on larger screens.
 *
 * @param props          The component props.
 * @param props.features An array of feature objects each containing image, icon, overline, title, and description content to render.
 * @param props.testId   A unique identifier for targeting the component in automated tests, defaulting to `'Features'`.
 * @param props.theme    The theme data attribute for switching between color schemes on the component container.
 * @returns A JSX element representing a grid of animated feature cards with imagery, icons, and structured text content.
 *
 * @example
 * ```tsx
 * <Features
 *   features={featuresData}
 *   testId="features-section"
 *   theme="dark"
 * />
 * ```
 */
const Features = observer(({features, testId = 'Features', theme}: ComponentProps) => {
    const {colors} = useFeatures();

    return (
        <Wrapper data-nfq-theme={theme}>
            <StyledContainer data-cy={testId}>
                <Row>
                    <Col
                        offset={{
                            xs: 0, xxl: 1
                        }}
                        xs={12}
                        xxl={11}
                    >
                        <GridWrapper>
                            {features?.map(feature => (
                                <GridItem key={feature.id}>
                                    <RealtiveImageWrapper>
                                        <StyledPicture
                                            image={feature.image}
                                            initial="outOfView"
                                            transition={{
                                                duration: 0.5,
                                                ease: 'easeInOut'
                                            }}
                                            variants={ImageViewAnimation}
                                            viewport={{margin: '0px 0px -20% 0px'}}
                                            whileInView="inView"
                                            isPresentational
                                        />
                                        {isDefined(feature.icon)
                                            && isDefinedAndPopulated(feature.icon.image)
                                            && (
                                                <IconWrap>
                                                    <StyledIcon>
                                                        <svg
                                                            aria-label={feature.icon.alt}
                                                            viewBox={`0 0 ${feature.icon.image.width} ${feature.icon.image.height}`}
                                                        >
                                                            <use
                                                                height={feature.icon.image.height ?? 'auto'}
                                                                href={`${feature.icon.image.url}#Icon`}
                                                                width={feature.icon.image.width ?? 'auto'}
                                                            />
                                                        </svg>
                                                    </StyledIcon>
                                                </IconWrap>
                                            )}
                                    </RealtiveImageWrapper>
                                    <InViewAnimContainer baseComponent={GridText}>
                                        <HeadlineWrapper>
                                            <MotionH6 variants={FadeInAnimation}>
                                                <Color $color={colors.secondary}>
                                                    {feature.overline}
                                                </Color>
                                            </MotionH6>
                                            <Spacer y={2} isNotStretching />
                                            <MotionH5 variants={FadeInAnimation}>
                                                {feature.title}
                                            </MotionH5>
                                        </HeadlineWrapper>
                                        <Spacer
                                            x={10}
                                            y={{
                                                xs: 6, lg: 0
                                            }}
                                            isNotStretching
                                        />
                                        <ColoredCol variants={FadeInAnimation}>
                                            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                                            {feature.description
                                                && <PayloadLexical description={feature.description} />}
                                        </ColoredCol>
                                    </InViewAnimContainer>
                                </GridItem>
                            ))}
                        </GridWrapper>
                    </Col>
                </Row>
            </StyledContainer>
        </Wrapper>
    );
});

Features.displayName = 'Features';

export {Features};

const Wrapper = styled.div`
    background-color: ${({theme}) => theme.colors.bgPrimary};
`;

const StyledContainer = styled(Container)`
    padding-block: ${spacing(14)};

    ${media('md')} {
        padding-block: ${spacing(20)};
    }
`;

const GridWrapper = styled.div`
    column-gap: var(--column-gap, 0px);
    display: grid;
    grid-auto-rows: auto;
    grid-template-columns: 1fr;
    grid-template-rows: fit-content auto fit-content auto;
    width: 100%;

    ${media('lg')} {
        grid-template-columns:
            calc(100% / 12 * 5 - var(--column-gap, 0px) + var(--column-gap, 0px) * 5 / 12)
            calc(100% / 12 * 7 - var(--column-gap, 0px) + var(--column-gap, 0px) * 7 / 12);
        grid-template-rows: fit-content auto;
    }

    ${media('xxl')} {
        grid-template-columns:
            calc(100% / 11 * 5 - var(--column-gap, 0px) + var(--column-gap, 0px) * 5 / 11)
            calc(100% / 11 * 6 - var(--column-gap, 0px) + var(--column-gap, 0px) * 6 / 11);
    }
`;

const GridItem = styled.div`
    display: grid;
    grid-auto-flow: row;
    grid-row: span 2;
    grid-template-rows: subgrid;
    width: 100%;

    &:first-child {
        padding-block-end: ${spacing(28)};
    }

    ${media('lg')} {
        &:first-child {
            padding-block-end: ${spacing(0)};
        }
    }
`;

const RealtiveImageWrapper = styled.div`
    height: 100%;
    position: relative;
`;

const StyledPicture = styled(motion.create(PayloadImage))`
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
`;

const IconWrap = styled.div`
    align-items: center;
    border: 2px solid ${({theme}) => theme.colors.bgPrimary};
    border-radius: 50%;
    bottom: 2rem;
    display: flex;
    height: 4.8rem;
    justify-content: center;
    left: 2rem;
    position: absolute;
    width: 4.8rem;

    ${media('lg')} {
        bottom: 3rem;
        height: 6rem;
        left: 3rem;
        width: 6rem;
    }
`;

const StyledIcon = styled.div`
    color: ${({theme}) => theme.colors.bgPrimary};
    height: 1.8rem;
    width: 1.8rem;

    ${media('lg')} {
        height: 3.6rem;
        width: 3.6rem;
    }
`;

const GridText = styled.div`
    display: flex;
    flex-direction: column;
    padding-block-start: ${spacing(8)};

    ${media('lg')} {
        flex-direction: row;
        padding-block-start: ${spacing(12)};
    }
`;

const HeadlineWrapper = styled.div`
    ${media('lg')} {
        flex: 0 0 calc(50% - 2rem);
    }
`;

const H6P = H6.withComponent('p');

const H5H2 = H5.withComponent('h2');

const MotionH6 = styled(motion.create(H6P), {...transient})``;

const MotionH5 = styled(motion.create(H5H2), {...transient})`
    color: ${({theme}) => theme.colors.primary};
`;

const ColoredCol = styled(motion.div)`
    color: ${({theme}) => theme.colors.secondary};
`;